import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import izenuAPI from '../../services/izenuAPI';
import csvExample from '../../assets/royalties-csv-exemplo.png';

// Streams → Royalties. Generates the monthly royalty reports (the same files as
// bin/generate_royalty_report.php) and lists every report with its downloads.
// api/admin/royalties/.

const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100';
const btnPrimary = 'rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium !text-white hover:bg-indigo-600 disabled:opacity-50';
const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const SCOPE_LABEL = { global: 'Geral', distributor: 'Distribuidora', artist: 'Artista', curators: 'Curadores' };
const usd = (n) => (n == null ? '—' : new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'USD' }).format(n));
const kz = (n) => (n == null ? '' : `${new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(n)} Kz`);
const monthLabel = (ym) => { const [y, m] = ym.split('-').map(Number); return `${MONTHS[m - 1]} ${y}`; };
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

async function download(file) {
    const res = await izenuAPI.get('/admin/royalties/download.php', { params: { file }, responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.split('/').pop();
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Section({ title, subtitle, children }) {
    return (
        <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {subtitle && <p className="mb-4 text-sm text-gray-500">{subtitle}</p>}
            {children}
        </section>
    );
}

export default function Royalties() {
    const [data, setData] = useState(null);       // { reports, distributors, last_closed_month }
    const [loadError, setLoadError] = useState('');
    const [showHow, setShowHow] = useState(false);

    const load = useCallback(() => {
        izenuAPI.get('/admin/royalties/list.php')
            .then(({ data: d }) => setData(d))
            .catch(() => setLoadError('Não foi possível carregar os relatórios.'));
    }, []);
    useEffect(() => { load(); }, [load]);

    if (!data) return <p className="mx-auto max-w-5xl p-6 text-sm text-gray-500">{loadError || 'A carregar…'}</p>;

    return (
        <div className="mx-auto max-w-5xl space-y-6 p-3 text-left sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Royalties de streaming</h1>
                    <p className="text-sm text-gray-500">Relatórios mensais de royalties de streaming, em CSV, por rede, distribuidora ou artista.</p>
                </div>
                <button type="button" onClick={() => setShowHow((v) => !v)} aria-expanded={showHow}
                    className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-800">
                    <span aria-hidden="true" className="flex h-4 w-4 items-center justify-center rounded-full border border-current text-[10px] font-bold">i</span>
                    {showHow ? 'Fechar explicação' : 'Como funciona'}
                </button>
            </div>
            {showHow && <HowItWorks />}
            <GenerateForm data={data} onDone={load} />
            <ClosePeriods periods={data.periods || []} onDone={load} />
            <ReportList reports={data.reports} />
        </div>
    );
}

function GenerateForm({ data, onDone }) {
    const [lastY, lastM] = data.last_closed_month.split('-').map(Number);
    const [year, setYear] = useState(lastY);
    const [month, setMonth] = useState(lastM);
    const [scope, setScope] = useState('all');
    const [distributorId, setDistributorId] = useState('');
    const [artist, setArtist] = useState(null);   // { user_id, name }
    const [artistQuery, setArtistQuery] = useState('');
    const [artistHits, setArtistHits] = useState([]);
    const [odoo, setOdoo] = useState({ loading: true });
    const [revenue, setRevenue] = useState('');
    const [rate, setRate] = useState('');
    const [revenueSource, setRevenueSource] = useState('odoo');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const timer = useRef(null);

    // Revenue and rate for the chosen month, from Odoo; editable.
    useEffect(() => {
        let alive = true;
        setOdoo({ loading: true });
        izenuAPI.get('/admin/royalties/revenue.php', { params: { month, year } })
            .then(({ data: d }) => {
                if (!alive) return;
                setOdoo({ loading: false, ...d });
                setRate(d.exchange_rate ? String(d.exchange_rate) : '');
                setRevenue(d.revenue ? String(d.revenue.revenue_usd) : '');
                setRevenueSource(d.revenue ? 'odoo' : 'manual');
            })
            .catch(() => alive && setOdoo({ loading: false, message: 'Não foi possível obter a receita da Odoo. Indica-a à mão.' }));
        return () => { alive = false; };
    }, [month, year]);

    const searchArtist = (v) => {
        setArtistQuery(v);
        setArtist(null);
        clearTimeout(timer.current);
        if (v.trim().length < 2) { setArtistHits([]); return; }
        timer.current = setTimeout(() => {
            izenuAPI.get('/artist/lookup.php', { params: { q: v.trim() } })
                .then(({ data: d }) => setArtistHits(Array.isArray(d?.data) ? d.data : []))
                .catch(() => setArtistHits([]));
        }, 250);
    };

    const years = useMemo(() => { const out = []; for (let y = lastY; y >= 2025; y--) out.push(y); return out; }, [lastY]);
    const isFuture = `${year}-${String(month).padStart(2, '0')}` > data.last_closed_month;
    const entityMissing = (scope === 'distributor' && !distributorId) || (scope === 'artist' && !artist);
    const odooRevenue = odoo.revenue?.revenue_usd;
    const canGenerate = !busy && !isFuture && !entityMissing && Number(revenue) > 0 && Number(rate) > 0;

    const generate = async () => {
        setBusy(true);
        setResult(null);
        try {
            const { data: d } = await izenuAPI.post('/admin/royalties/generate.php', {
                month, year, scope,
                entity_id: scope === 'distributor' ? Number(distributorId) : scope === 'artist' ? artist.user_id : undefined,
                revenue_usd: Number(revenue), exchange_rate: Number(rate),
                revenue_source: revenueSource === 'odoo' && Number(revenue) === odooRevenue ? 'odoo' : 'manual',
            });
            setResult(d);
            onDone();
        } catch (err) {
            setResult({ response: false, errors: [err?.response?.data?.message || 'Não foi possível gerar.'] });
        }
        setBusy(false);
    };

    const label = (t) => <span className="mb-1 block text-sm font-medium text-gray-700">{t}</span>;

    return (
        <Section title="Gerar relatório" subtitle="Escolhe o mês e o âmbito. O valor de cada stream sai sempre da rede inteira: o relatório de uma distribuidora ou de um artista é a fatia dele nesse cálculo.">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                <label className="block sm:col-span-1">{label('Mês')}
                    <select className={inputCls} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select></label>
                <label className="block sm:col-span-1">{label('Ano')}
                    <select className={inputCls} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select></label>
                <label className="block sm:col-span-2">{label('Âmbito')}
                    <select className={inputCls} value={scope} onChange={(e) => setScope(e.target.value)}>
                        <option value="all">Geral + todas as distribuidoras (como o cron)</option>
                        <option value="global">Só o geral</option>
                        <option value="distributor">Uma distribuidora</option>
                        <option value="artist">Um artista</option>
                    </select></label>

                {scope === 'distributor' && (
                    <label className="block sm:col-span-2">{label('Distribuidora')}
                        <select className={inputCls} value={distributorId} onChange={(e) => setDistributorId(e.target.value)}>
                            <option value="">Escolhe…</option>
                            {data.distributors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select></label>
                )}
                {scope === 'artist' && (
                    <div className="relative sm:col-span-2">{label('Artista')}
                        <input className={inputCls} value={artist ? artist.name : artistQuery} placeholder="Procurar artista…"
                            onChange={(e) => searchArtist(e.target.value)} />
                        {!artist && artistHits.length > 0 && (
                            <ul className="absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-lg !border !border-solid border-gray-200 bg-white shadow-lg [&_li]:!p-0">
                                {artistHits.map((h) => (
                                    <li key={h.user_id}><button type="button" onClick={() => { setArtist(h); setArtistHits([]); }}
                                        className="w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-indigo-50">{h.name}</button></li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 rounded-lg bg-gray-50 p-4 sm:grid-cols-2">
                <label className="block">{label('Receita de streaming no mês (USD)')}
                    <input className={inputCls} type="number" min="0" step="0.01" value={revenue}
                        onChange={(e) => { setRevenue(e.target.value); setRevenueSource('manual'); }} />
                    <span className="mt-1 block text-xs text-gray-500">
                        {odoo.loading ? 'A consultar a Odoo…'
                            : odoo.revenue ? <>Odoo: {usd(odoo.revenue.revenue_usd)} em {odoo.revenue.invoices} faturas pagas de assinaturas Premium e publicidade, sem IVA. Activações por código promocional ou pontos não contam.
                                {Number(revenue) !== odoo.revenue.revenue_usd && <> <button type="button" className="text-indigo-600 underline" onClick={() => { setRevenue(String(odoo.revenue.revenue_usd)); setRevenueSource('odoo'); }}>Usar valor da Odoo</button></>}</>
                                : (odoo.message || 'Sem receita da Odoo para este mês.')}
                    </span>
                    <span className="mt-1 block text-xs text-gray-500">Os artistas recebem 70% deste valor, divididos pelos streams do mês.</span>
                </label>
                <label className="block">{label('Câmbio USD → Kz')}
                    <input className={inputCls} type="number" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} />
                    <span className="mt-1 block text-xs text-gray-500">Taxa actual na Odoo. Os valores em Kz do relatório usam esta taxa.</span>
                </label>
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                <p className="flex-1 text-xs text-gray-500">
                    {isFuture ? 'Só se geram meses já terminados.'
                        : 'Gerar de novo o mesmo mês e âmbito substitui o relatório anterior. Os ganhos dos curadores desse mês são recalculados. Um mês já fechado não se gera de novo.'}
                </p>
                <button type="button" className={btnPrimary} disabled={!canGenerate} onClick={generate}>
                    {busy ? 'A gerar…' : 'Gerar relatório'}
                </button>
            </div>

            {result && (
                <div className={`mt-4 rounded-lg p-3 text-sm ${result.response ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
                    {result.response ? <>Gerado: {result.files.length} ficheiros. Estão na lista abaixo.</> : (result.errors || []).map((e) => <div key={e}>{e}</div>)}
                </div>
            )}
        </Section>
    );
}

// A month's global report writes what it owes each artist (royalty_statements).
// Closing the month puts those amounts in the balances of artists and
// distributors (Financeiro → Pagamentos a artistas), and the month can no longer
// be regenerated. There is no reopening.
function ClosePeriods({ periods, onDone }) {
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    if (periods.length === 0) return null;

    const close = async (p) => {
        if (!window.confirm(`Fechar ${monthLabel(p.period)}?\n\n${kz(Number(p.amount_aoa))} de royalties entram no saldo de ${p.artists} artistas e distribuidoras, e o mês não pode voltar a ser gerado. Não se pode desfazer.`)) return;
        setBusy(p.period); setError('');
        try {
            await izenuAPI.post('/admin/royalties/close.php', { period: p.period });
            onDone();
        } catch (err) {
            setError(err?.response?.data?.message || 'Não foi possível fechar o mês.');
        }
        setBusy('');
    };

    return (
        <Section title="Fechar o mês" subtitle="O relatório geral calcula o que cada artista recebe. Confere-o e fecha o mês: os royalties entram no saldo dos artistas (ou da distribuidora, se for ela a receber) e passam a poder ser levantados. Um mês fechado não volta a ser gerado.">
            <div className="overflow-x-auto">
                <table className="w-full min-w-[600px] text-sm">
                    <thead><tr className="text-left text-xs uppercase text-gray-500">
                        <th className="py-2 pr-4 font-semibold">Mês</th><th className="py-2 pr-4 text-right font-semibold">Artistas</th>
                        <th className="py-2 pr-4 text-right font-semibold">Royalties</th><th className="py-2 pr-4 text-right font-semibold">Via distribuidoras</th>
                        <th className="py-2 pr-4 font-semibold">Gerado</th><th className="py-2 text-right font-semibold"></th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100">
                        {periods.map((p) => (
                            <tr key={p.period}>
                                <td className="py-2 pr-4 font-medium text-gray-900">{monthLabel(p.period)}</td>
                                <td className="py-2 pr-4 text-right tabular-nums text-gray-600">{p.artists}</td>
                                <td className="py-2 pr-4 text-right font-semibold tabular-nums text-gray-900">{kz(Number(p.amount_aoa))}</td>
                                <td className="py-2 pr-4 text-right tabular-nums text-gray-600">{p.closed_at ? kz(Number(p.via_distributors_aoa)) : '—'}</td>
                                <td className="py-2 pr-4 text-xs text-gray-500">{p.generated_at?.slice(0, 16)}{p.generated_by ? ` · ${p.generated_by}` : ''}</td>
                                <td className="py-2 text-right">
                                    {p.closed_at
                                        ? <span className="text-xs font-semibold text-green-700">Fechado a {p.closed_at.slice(0, 10)}</span>
                                        : <button type="button" className={btnPrimary} disabled={!!busy} onClick={() => close(p)}>{busy === p.period ? 'A fechar…' : 'Fechar mês'}</button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        </Section>
    );
}

function ReportList({ reports }) {
    const periods = useMemo(() => Array.from(new Set(reports.map((r) => r.period))), [reports]);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [scope, setScope] = useState('');
    const [busyFile, setBusyFile] = useState('');

    const shown = reports.filter((r) => (!from || r.period >= from) && (!to || r.period <= to) && (!scope || r.scope === scope));
    const byPeriod = shown.reduce((acc, r) => { (acc[r.period] = acc[r.period] || []).push(r); return acc; }, {});

    const get = async (file) => {
        setBusyFile(file);
        try { await download(file); } catch { alert('Não foi possível descarregar.'); }
        setBusyFile('');
    };

    return (
        <Section title="Relatórios" subtitle="Todos os relatórios gerados, pelo admin ou pela linha de comandos. O CSV abre no Excel (separador ; e vírgula decimal).">
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label className="block"><span className="mb-1 block text-xs text-gray-500">De</span>
                    <select className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)}>
                        <option value="">Início</option>{[...periods].reverse().map((p) => <option key={p} value={p}>{monthLabel(p)}</option>)}
                    </select></label>
                <label className="block"><span className="mb-1 block text-xs text-gray-500">Até</span>
                    <select className={inputCls} value={to} onChange={(e) => setTo(e.target.value)}>
                        <option value="">Hoje</option>{periods.map((p) => <option key={p} value={p}>{monthLabel(p)}</option>)}
                    </select></label>
                <label className="block"><span className="mb-1 block text-xs text-gray-500">Âmbito</span>
                    <select className={inputCls} value={scope} onChange={(e) => setScope(e.target.value)}>
                        <option value="">Todos</option>{Object.entries(SCOPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select></label>
            </div>

            {shown.length === 0 ? <p className="text-sm text-gray-500">Nenhum relatório com estes filtros.</p> : (
                <div className="space-y-5">
                    {Object.entries(byPeriod).map(([period, list]) => {
                        // The month's network figures are the same in every report of the month.
                        const ref = list.find((r) => r.revenue_usd != null) || {};
                        const hasIndependents = list.some((r) => r.scope === 'global') && list.some((r) => r.scope === 'distributor');
                        return (
                        <div key={period}>
                            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4">
                                <h3 className="text-sm font-semibold text-gray-900">{monthLabel(period)}</h3>
                                {ref.revenue_usd != null && (
                                    <p className="text-xs text-gray-500">
                                        Receita da rede {usd(ref.revenue_usd)}{ref.revenue_source === 'manual' && ' (manual)'}
                                        {ref.pool_usd != null && <> · para artistas (70%) {usd(ref.pool_usd)}</>}
                                        {ref.exchange_rate && <> · câmbio {ref.exchange_rate}</>}
                                    </p>
                                )}
                            </div>
                            <ul className="divide-y divide-gray-100 rounded-lg !border !border-solid border-gray-200 [&>li]:!p-0">
                                {list.map((r) => (
                                    <li key={r.file}>
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 text-sm">
                                            <span className="w-24 shrink-0 text-xs font-semibold uppercase text-gray-500">{SCOPE_LABEL[r.scope]}</span>
                                            <span className="min-w-0 flex-1 truncate text-gray-900">{r.scope === 'global' ? 'Toda a rede' : r.entity}</span>
                                            <span className="text-right text-sm">
                                                {r.rows === 0
                                                    ? <span className="text-gray-400">Sem streams no mês</span>
                                                    : r.pay_usd != null && <><span className="font-semibold tabular-nums text-gray-900">{usd(r.pay_usd)}</span> <span className="text-xs text-gray-500">{kz(r.pay_aoa)} a pagar</span></>}
                                            </span>
                                            <span className="text-xs text-gray-400">
                                                {r.generated_by ? `por ${r.generated_by} · ` : ''}{r.modified_at.slice(0, 16)}
                                            </span>
                                            <span className="flex gap-2">
                                                <button type="button" onClick={() => get(r.file)} disabled={busyFile === r.file}
                                                    className="rounded-md bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100">
                                                    {busyFile === r.file ? '…' : `CSV · ${fmtSize(r.size)}`}
                                                </button>
                                                {r.summary && (
                                                    <button type="button" onClick={() => get(r.summary)} disabled={busyFile === r.summary}
                                                        className="rounded-md px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100">Resumo</button>
                                                )}
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            {hasIndependents && (
                                <p className="mt-1.5 text-xs text-gray-400">Artistas sem distribuidora só aparecem no relatório geral.</p>
                            )}
                        </div>
                        );
                    })}
                </div>
            )}
        </Section>
    );
}

// The rules below mirror classes/MonetizationCalculator.php — keep them in step.
const POINT_RULES = [
    ['Artista profissional + audição activa', '4', '1 × 2 (profissional) × 2 (activa)'],
    ['Audição activa ou artista profissional', '2', '1 × 2 (um dos dois)'],
    ['Audição comum', '1', 'Rádio, reprodução automática, playlists de terceiros'],
    ['Ruído', '0,5', 'Conteúdo funcional (chuva, ruído branco), sem multiplicadores'],
    ['Fraude', '0', 'Mais de 300 audições num dia pelo mesmo ouvinte: esse dia inteiro é ignorado'],
];

/** "Como funciona": the royalty model, opened from a quiet button in the header. */
function HowItWorks() {
    const cell = 'px-3 py-2 text-sm';
    return (
        <Section title="Como funciona o modelo de royalties"
            subtitle="Cada audição não vale o mesmo: a intenção de quem ouve define quantos pontos a audição vale, e a receita do mês é dividida por todos os pontos.">
            <div className="space-y-6 text-sm text-gray-700">
                <ol className="grid grid-cols-1 gap-3 sm:grid-cols-3 [&>li]:!p-0">
                    {[
                        ['1. Receita do mês', 'Assinaturas Premium e publicidade pagas no mês, sem IVA. Códigos promocionais, pontos e compras de teste não contam.'],
                        ['2. Parte dos artistas', '70% dessa receita vai para os artistas. Os outros 30% ficam com a Izenu, que paga daí aos curadores de playlists aprovados 2% do valor das audições vindas das playlists deles.'],
                        ['3. Valor do ponto', 'Parte dos artistas ÷ total de pontos do mês. Cada faixa recebe pontos × valor do ponto.'],
                    ].map(([t, d]) => (
                        <li key={t} className="rounded-lg bg-gray-50 p-3">
                            <p className="font-semibold text-gray-900">{t}</p>
                            <p className="mt-1 text-xs leading-relaxed text-gray-600">{d}</p>
                        </li>
                    ))}
                </ol>

                <div>
                    <h3 className="mb-2 font-semibold text-gray-900">Pontos por audição</h3>
                    <div className="overflow-x-auto rounded-lg !border !border-solid border-gray-200">
                        <table className="w-full min-w-[520px]">
                            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                                <tr><th className={cell}>Tipo de audição</th><th className={`${cell} text-right`}>Pontos</th><th className={cell}>Como se compõe</th></tr>
                            </thead>
                            <tbody>
                                {POINT_RULES.map(([t, p, d]) => (
                                    <tr key={t} className="border-t border-gray-100">
                                        <td className={`${cell} font-medium text-gray-900`}>{t}</td>
                                        <td className={`${cell} text-right font-semibold tabular-nums`}>{p}</td>
                                        <td className={`${cell} text-gray-600`}>{d}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <ul className="mt-2 space-y-1 text-xs text-gray-500 [&>li]:!p-0">
                        <li><strong className="text-gray-700">Audição activa</strong>: começou numa pesquisa, na biblioteca ou no perfil do artista — a pessoa foi à procura.</li>
                        <li><strong className="text-gray-700">Artista profissional</strong>: pelo menos 300 audições e 40 ouvintes diferentes no mês.</li>
                        <li><strong className="text-gray-700">Limites</strong>: por ouvinte e por dia contam no máximo 5 audições da mesma faixa e 30 do mesmo artista; o excesso é ignorado, o ouvinte não.</li>
                        <li><strong className="text-gray-700">Não contam</strong>: as audições do próprio artista, ouvintes excluídos das estatísticas e audições com defeito.</li>
                    </ul>
                </div>

                <div>
                    <h3 className="mb-2 font-semibold text-gray-900">Exemplo</h3>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {[
                            ['Tony Amado · "Mbolototo"', 'audição comum', '1', '1 × 0,4416 = 0,44 USD ≈ 405 Kz'],
                            ['Saka-Handa · "Ndukusole"', 'audição activa + artista profissional', '1 × 2 × 2 = 4', '4 × 0,4416 = 1,77 USD ≈ 1 619 Kz'],
                        ].map(([who, kind, pts, total]) => (
                            <div key={who} className="rounded-lg !border !border-solid border-gray-200 p-3">
                                <p className="font-semibold text-gray-900">{who}</p>
                                <p className="text-xs text-gray-500">{kind}</p>
                                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                                    <dt className="text-gray-500">Pontos</dt><dd className="tabular-nums">{pts}</dd>
                                    <dt className="text-gray-500">Valor do ponto</dt><dd className="tabular-nums">0,4416 USD</dd>
                                    <dt className="text-gray-500">Royalty</dt><dd className="font-semibold tabular-nums">{total}</dd>
                                </dl>
                            </div>
                        ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                        O valor do ponto é o mesmo para toda a gente no mês. A diferença está em quantos pontos cada audição gera:
                        a faixa da Saka-Handa rendeu 4 vezes mais com uma única audição.
                    </p>
                </div>

                <div>
                    <h3 className="mb-2 font-semibold text-gray-900">Como ler o CSV</h3>
                    <a href={csvExample} target="_blank" rel="noreferrer" title="Abrir em tamanho real"
                        className="mb-3 block overflow-x-auto rounded-lg !border !border-solid border-gray-200 bg-white">
                        <img src={csvExample} alt="Excerto de um CSV de royalties de Junho de 2025 com as colunas anotadas"
                            className="block w-full min-w-[720px]" />
                    </a>
                    <p className="mb-3 text-xs text-gray-500">Excerto real de Junho de 2025. Clica na imagem para a abrir em tamanho real.</p>
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
                        {[
                            ['Interaction Mode', 'Active (pesquisa, biblioteca, perfil) ou Passive'],
                            ['Points', 'Pontos de cada audição dessa linha'],
                            ['Items', 'Número de audições agrupadas na linha'],
                            ['Unit Rate', 'Pontos × valor do ponto, em USD'],
                            ['Total Royalty', 'Unit Rate × Items, em USD'],
                            ['Total Royalty Kz', 'O mesmo, em kwanzas ao câmbio usado'],
                        ].map(([k, v]) => (
                            <div key={k} className="flex gap-2"><dt className="w-32 shrink-0 font-medium text-gray-700">{k}</dt><dd className="text-gray-600">{v}</dd></div>
                        ))}
                    </dl>
                </div>

                <p className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
                    <strong className="text-gray-800">Porque este modelo?</strong> Justiça por intenção (quem procura um artista conta mais
                    do que a reprodução automática), recompensa o crescimento (o bónus de artista profissional) e resiste a fraude
                    (limites diários e o corte acima de 300 audições por dia).
                </p>
            </div>
        </Section>
    );
}
